import type { JSX } from "react";

import type { WeatherGlyphKind } from "@/lib/weather";

/** The cloud body every overcast shape shares. */
const CLOUD = <path d="M17.2 16.5H7.4a4 4 0 0 1-.6-7.9A5.4 5.4 0 0 1 17.2 10.6a3 3 0 0 1 0 5.9Z" />;

const SUN = (
  <>
    <circle cx="12" cy="10.4" r="3.4" />
    <path d="M12 5V3.4M12 17.4v-1.6M6.6 10.4H5M19 10.4h-1.6M8.2 6.6 7 5.4M17 15.4l-1.2-1.2M15.8 6.6 17 5.4M7 15.4l1.2-1.2" />
  </>
);

const MOON = <path d="M14.6 3.6a8 8 0 1 0 5.8 13.2A8.4 8.4 0 0 1 14.6 3.6Z" />;

/** The icons one kind can draw: its day shape, and a night one where it differs. */
interface GlyphShapes {
  day: JSX.Element;
  night?: JSX.Element;
}

/** The condition icon's props: which shape, in which half of the day. */
interface WeatherGlyphProps {
  kind: WeatherGlyphKind;
  night: boolean;
}

const SHAPE: Record<WeatherGlyphKind, GlyphShapes> = {
  clear: { day: SUN, night: MOON },
  partly: {
    day: (
      <>
        <path d="M15.9 4.4v1.4M19.6 8.1h1.4M18.5 5.5l1-1" />
        <circle cx="15.9" cy="8.1" r="2.5" />
        {CLOUD}
      </>
    ),
    night: (
      <>
        <path d="M16.6 3.2a5.4 5.4 0 1 0 3.9 8.9 5.7 5.7 0 0 1-3.9-8.9Z" />
        {CLOUD}
      </>
    ),
  },
  cloud: { day: CLOUD },
  fog: {
    day: (
      <>
        {CLOUD}
        <path d="M8 19.4h8M10 21.8h6" />
      </>
    ),
  },
  rain: {
    day: (
      <>
        {CLOUD}
        <path d="M9.4 18.4 8.5 21M14.6 18.4 13.7 21" />
      </>
    ),
  },
  sleet: {
    day: (
      <>
        {CLOUD}
        <path d="M9.4 18.4 8.5 21M14.5 19.3v2.2M13.4 20.4h2.2" />
      </>
    ),
  },
  snow: {
    day: (
      <>
        {CLOUD}
        <path d="M9.4 19.3v2.2M8.3 20.4h2.2M14.5 19.3v2.2M13.4 20.4h2.2" />
      </>
    ),
  },
  thunder: {
    day: (
      <>
        {CLOUD}
        <path d="M13.4 18.2 11.2 21.4h2.2l-1 2.4" />
      </>
    ),
  },
};

/**
 * One condition icon, drawn as stroke paths in `currentColor` so it inherits the
 * row's colour and scales with the mono meta text beside it. Decorative: the
 * temperature it sits beside carries the reading for a screen reader.
 */
export function WeatherGlyph({ kind, night }: WeatherGlyphProps): JSX.Element {
  const shape = SHAPE[kind];
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {night && shape.night ? shape.night : shape.day}
    </svg>
  );
}
